// src/components/FileUploadSystem.js (modified)
import React, { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged, signInWithPopup, GoogleAuthProvider, signOut } from 'firebase/auth';
import { uploadFile, getFiles, deleteFile } from '../firebase/storage';
import { createOrUpdateUser, getUserById } from '../firebase/users'; // Import user functions
import FileUploader from './FileUploader';
import FileList from './FileList';
import CommentCitationSystem from './CommentCitationSystem';

const FileUploadSystem = () => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null); // Store Firestore user data
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('files');
  
  const auth = getAuth();
  
  // Handle auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (authUser) {
        setUser(authUser);
        try {
          // Create or update user in Firestore
          const firestoreUser = await createOrUpdateUser(authUser);
          setUserData(firestoreUser);
          loadFiles();
        } catch (err) {
          console.error("Error setting up user:", err);
          setError("Failed to set up user profile. Some features may be limited.");
        }
      } else {
        setUser(null);
        setUserData(null);
        setFiles([]);
        setIsLoading(false);
      }
    });
    
    return () => unsubscribe();
  }, [auth]);
  
  // Load files
  const loadFiles = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const filesData = await getFiles(user.uid);
      setFiles(filesData);
    } catch (err) {
      console.error("Error loading files:", err);
      setError("Failed to load files. Please refresh the page to try again.");
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle file upload
  const handleFileUpload = async (file) => {
    if (!user) return;
    
    try {
      await uploadFile(file, user.uid);
      loadFiles(); // Refresh the file list
    } catch (err) {
      console.error("Error uploading file:", err);
      setError("Failed to upload file. Please try again.");
    }
  };
  
  // Handle file deletion
  const handleFileDelete = async (fileId) => {
    if (!user) return;
    
    try {
      await deleteFile(fileId, user.uid);
      loadFiles(); // Refresh the file list
    } catch (err) {
      console.error("Error deleting file:", err);
      setError("Failed to delete file. Please try again.");
    }
  };
  
  // Handle user sign in
  const handleSignIn = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      // User is automatically handled in the onAuthStateChanged listener
    } catch (err) {
      console.error("Error signing in:", err);
      setError("Failed to sign in. Please try again.");
    }
  };
  
  // Handle user sign out
  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.error("Error signing out:", err);
      setError("Failed to sign out. Please try again.");
    }
  };
  
  return (
    <div className="file-upload-system">
      {error && (
        <div className="error-banner">
          {error}
          <button 
            className="close-button" 
            onClick={() => setError(null)}
          >
            ×
          </button>
        </div>
      )}
      
      {/* Auth section */}
      <div className="auth-section">
        {user ? (
          <div className="user-info">
            <div className="user-details">
              <img src={user.photoURL} alt={user.displayName} className="user-avatar" />
              <div>
                <p className="user-name">{user.displayName}</p>
                <p className="user-email">{user.email}</p>
              </div>
            </div>
            <button 
              onClick={handleSignOut}
              className="sign-out-button"
            >
              Sign Out
            </button>
          </div>
        ) : (
          <div className="sign-in-container">
            <p>Sign in to upload and manage files</p>
            <button 
              onClick={handleSignIn}
              className="sign-in-button"
            >
              Sign in with Google
            </button>
          </div>
        )}
      </div>
      
      {/* Tabs section */}
      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'files' ? 'active' : ''}`}
          onClick={() => setActiveTab('files')}
        >
          Files
        </button>
        <button 
          className={`tab ${activeTab === 'comments' ? 'active' : ''}`}
          onClick={() => setActiveTab('comments')}
        >
          Comments & Citations
        </button>
      </div>
      
      {/* Content based on active tab */}
      <div className="tab-content">
        {activeTab === 'files' ? (
          <div className="files-section">
            {user && <FileUploader onFileUpload={handleFileUpload} />}
            <FileList 
              files={files} 
              onDeleteFile={handleFileDelete} 
              isLoading={isLoading}
              isAuthenticated={!!user}
            />
          </div>
        ) : (
          <CommentCitationSystem user={user} userData={userData} />
        )}
      </div>
    </div>
  );
};

export default FileUploadSystem;